import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BlockNoteEditor } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { Button, Group, TextInput, ActionIcon, Box, Notification } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useMantineColorScheme } from '@mantine/core';
import { IconPlus, IconTrash, IconDeviceFloppy, IconFileText, IconBold, IconItalic, IconH1, IconH2 } from '@tabler/icons-react';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import { savePageContent, loadPageContent, deletePageContent } from '../../../core/workspace/pageContentStorage';

/**
 * Minimal Editor Toolbar
 */
const EditorToolbar: React.FC<{ editor: BlockNoteEditor | null; colorScheme?: string }> = ({ editor, colorScheme }) => {
  if (!editor) return null;
  
  const isDark = colorScheme === 'dark';
  const bgColor = isDark ? '#292929' : '#fafafa';
  const borderColor = isDark ? '#3c4043' : '#e9e9e7';
  const iconColor = isDark ? '#9aa0a6' : '#5f6368';

  const buttons = [
    { icon: IconBold, label: 'Bold', action: () => editor.toggleBold() },
    { icon: IconItalic, label: 'Italic', action: () => editor.toggleItalic() },
    { icon: IconH1, label: 'Heading 1', action: () => editor.createBlock({
      content: 'Heading',
    })},
    { icon: IconH2, label: 'Heading 2', action: () => {} },
  ];

  return (
    <Box
      style={{
        display: 'flex',
        gap: 4,
        padding: '8px 12px',
        borderBottom: `1px solid ${borderColor}`,
        backgroundColor: bgColor
      }}
    >
      {buttons.map((btn, i) => (
        <ActionIcon
          key={i}
          variant="subtle"
          size="sm"
          onClick={btn.action}
          title={btn.label}
          style={{ color: iconColor }}
        >
          <btn.icon size={16} />
        </ActionIcon>
      ))}
    </Box>
  );
};

interface PageEditorProps {
  className?: string;
}

export const PageEditor: React.FC<PageEditorProps> = ({ className = '' }) => {
  const { currentPageId, currentPage, renamePage, createPage, deletePage } = useWorkspace();
  const { colorScheme } = useMantineColorScheme();
  const [editor, setEditor] = useState<BlockNoteEditor | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [pageTitle, setPageTitle] = useState('Untitled Page');
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  // Track current content for save button state
  const [editorContent, setEditorContent] = useState<string>('[]');
  const editorRef = useRef<BlockNoteEditor | null>(null);
  const lastContentRef = useRef<string>('');
  
  // Dark mode colors
  const bgColor = colorScheme === 'dark' ? '#1f1f1f' : '#fafafa';
  const headerBg = colorScheme === 'dark' ? '#292929' : '#ffffff';
  const textColor = colorScheme === 'dark' ? '#e8eaed' : '#37352f';
  const mutedColor = colorScheme === 'dark' ? '#9aa0a6' : '#8e8e8e';
  const borderColor = colorScheme === 'dark' ? '#3c4043' : '#e9e9e7';

  // Update title when current page changes
  useEffect(() => {
    if (currentPage) {
      setPageTitle(currentPage.title);
      setIsDirty(false);
      // Check if page has content
      const content = loadPageContent(currentPageId);
      setHasContent(content && content.length > 0);
    }
  }, [currentPage, currentPageId]);

  // Initialize editor for current page
  const initEditorForPage = useCallback(async (pageId: string) => {
    setIsLoading(true);
    try {
      // Destroy existing editor safely
      (editorRef.current as any)?._rootElement?.remove?.();
      editorRef.current = null;

      // Load existing content for this page
      const existingContent = loadPageContent(pageId);
      
      // Create new editor
      const newEditor = await BlockNoteEditor.create({
        initialContent: existingContent || undefined,
      });

      editorRef.current = newEditor;
      setEditor(newEditor);
      setIsDirty(false);
      
      // Track if content exists
      if (existingContent && existingContent.length > 0) {
        setHasContent(true);
        lastContentRef.current = JSON.stringify(existingContent);
        setEditorContent(JSON.stringify(existingContent));
      } else {
        setHasContent(false);
        lastContentRef.current = '';
        setEditorContent('[]');
      }

      // Set up content change listener
      newEditor.onEditorContentChange(() => {
        const currentContent = JSON.stringify(newEditor.topLevelBlocks);
        
        // Only set dirty if content actually changed from last save
        if (currentContent !== lastContentRef.current) {
          setIsDirty(true);
          lastContentRef.current = currentContent;
          const hasBlocks = currentContent !== '[]';
          setHasContent(hasBlocks);
          setEditorContent(currentContent);
          
          // Auto-save to localStorage when there are blocks
          const blocks = newEditor.topLevelBlocks;
          if (blocks.length > 0) {
            savePageContent(pageId, blocks);
            setIsDirty(false); // Auto-saved
          }
        }
      });

    } catch (err) {
      console.error('Failed to initialize editor:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize editor on mount and when page changes
  useEffect(() => {
    if (currentPageId) {
      initEditorForPage(currentPageId);
    }

    return () => {
      if (editorRef.current) {
        // Save current content before unmounting
        try {
          const blocks = editorRef.current.topLevelBlocks;
          if (blocks.length > 0 && currentPageId) {
            savePageContent(currentPageId, blocks);
          }
          if (typeof editorRef.current.destroy === 'function') {
            editorRef.current.destroy();
          }
        } catch (e) {
          // Ignore cleanup errors
        }
        editorRef.current = null;
      }
    };
  }, [currentPageId, initEditorForPage]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageTitle(e.target.value);
    setIsDirty(true);
  };

  const handleTitleSave = async () => {
    if (!currentPageId || pageTitle === currentPage?.title) {
      setIsEditingTitle(false);
      return;
    }
    try {
      await renamePage(currentPageId, pageTitle);
      setIsDirty(false);
    } catch (err) {
      console.error('Failed to rename page:', err);
    }
    setIsEditingTitle(false);
  };

  const handleSave = async () => {
    if (!editorRef.current || !currentPageId) return;
    try {
      const blocks = editorRef.current.topLevelBlocks;
      savePageContent(currentPageId, blocks);
      setIsDirty(false);
      setHasContent(blocks.length > 0);
      lastContentRef.current = JSON.stringify(blocks);
      setEditorContent(JSON.stringify(blocks));
      // Show save confirmation
      setShowSaveNotification(true);
      setTimeout(() => setShowSaveNotification(false), 2000);
    } catch (err) {
      console.error('Failed to save page:', err);
    }
  };
  
  const [showSaveNotification, setShowSaveNotification] = useState(false);

  const handleDelete = async () => {
    if (!currentPageId) return;
    if (!confirm('Delete this page? This cannot be undone.')) return;
    try {
      deletePageContent(currentPageId);
      await deletePage(currentPageId);
    } catch (err) {
      console.error('Failed to delete page:', err);
    }
  };

  const handleCreateNew = async () => {
    try {
      await createPage('Untitled');
      setPageTitle('Untitled');
      setIsDirty(false);
      setHasContent(false);
    } catch (err) {
      console.error('Failed to create page:', err);
    }
  };

  // Calculate if save button should be enabled
  const shouldEnableSave = isDirty || hasContent;

  return (
    <Box
      className={className}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: bgColor
      }}
    >
      {showSaveNotification && (
        <Notification
          icon={<IconCheck size={16} />}
          color="teal"
          title="Saved"
          withCloseButton={false}
          style={{ position: 'absolute', top: 60, right: 16, zIndex: 1000 }}
        >
          Page saved successfully
        </Notification>
      )}
      
      {/* Page Header */}
      <Box p="md" style={{ backgroundColor: headerBg, borderBottom: `1px solid ${borderColor}` }}>
        <Group justify="space-between" align="center">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <IconFileText size={20} color={mutedColor} style={{ flexShrink: 0 }} />
            
            {isEditingTitle ? (
              <TextInput
                value={pageTitle}
                onChange={handleTitleChange}
                onBlur={handleTitleSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') {
                    setPageTitle(currentPage?.title || 'Untitled');
                    setIsEditingTitle(false);
                  }
                }}
                variant="filled"
                size="md"
                autoFocus
                styles={{ input: { fontSize: 18, fontWeight: 600, color: textColor } }}
              />
            ) : (
              <h2
                onClick={() => setIsEditingTitle(true)}
                style={{
                  cursor: 'pointer',
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: textColor,
                  lineHeight: 1.2
                }}
              >
                {pageTitle}
              </h2>
            )}
            
            {isDirty && (
              <span style={{ color: '#f59e0b', fontSize: 12 }}>●</span>
            )}
          </div>

          <Group gap="xs">
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<IconPlus size={14} />}
              onClick={handleCreateNew}
            >
              New
            </Button>
            
            {currentPageId && (
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
            
            <Button
              size="xs"
              variant="filled"
              color="blue"
              leftSection={<IconDeviceFloppy size={14} />}
              onClick={handleSave}
              disabled={!shouldEnableSave}
            >
              Save
            </Button>
          </Group>
        </Group>
      </Box>

      {/* Toolbar */}
      <EditorToolbar editor={editor} colorScheme={colorScheme} />

      {/* Editor Content */}
      <Box style={{ flex: 1, overflow: 'auto', backgroundColor: headerBg }}>
        {isLoading ? (
          <Box p="xl" style={{ textAlign: 'center', color: mutedColor }}>
            Loading content...
          </Box>
        ) : editor ? (
          <Box style={{ maxWidth: 720, margin: '0 auto', padding: '24px 24px' }}>
            <BlockNoteView
              editor={editor}
              theme="light"
              style={{
                minHeight: 'calc(100vh - 300px)',
                outline: 'none'
              }}
            />
          </Box>
        ) : (
          <Box p="xl" style={{ textAlign: 'center', color: '#8e8e8e' }}>
            No page selected
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PageEditor;